'use client';

import { useState, useEffect, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { cn } from '@medizzconnect/shared/utils';

interface FavoriteButtonProps {
  profileId: string;
  userId?: string | null;
  /** Kompakter Modus: nur Herz-Icon, kein Text (für Video-Card-Overlay) */
  compact?: boolean;
  /** Zusätzliche CSS-Klassen für den Button */
  className?: string;
}

export function FavoriteButton({ profileId, userId, compact = false, className }: FavoriteButtonProps) {
  const t = useTranslations('videoModal');
  const [isFavorite, setIsFavorite] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Check if already favorited
  useEffect(() => {
    if (!userId) return;
    fetch(`/api/favorites?profileId=${profileId}`)
      .then((r) => r.json())
      .then((data: { isFavorite?: boolean }) => {
        setIsFavorite(data.isFavorite ?? false);
      })
      .catch(() => {});
  }, [userId, profileId]);

  const toggleFavorite = useCallback(async () => {
    if (!userId || isLoading) return;

    setIsLoading(true);
    try {
      const res = await fetch('/api/favorites', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profileId }),
      });
      const data = (await res.json()) as { isFavorite?: boolean };
      setIsFavorite(data.isFavorite ?? false);
    } catch {
      // Silent fail
    } finally {
      setIsLoading(false);
    }
  }, [userId, profileId, isLoading]);

  if (!userId) return null;

  if (compact) {
    return (
      <button
        onClick={(e) => { e.stopPropagation(); toggleFavorite(); }}
        disabled={isLoading}
        className={cn(
          'flex h-8 w-8 items-center justify-center rounded-full shadow-md transition-all',
          isFavorite
            ? 'bg-red-500 text-white hover:bg-red-600'
            : 'bg-white/90 text-gray-500 hover:bg-white hover:text-red-500 backdrop-blur-sm'
        )}
        aria-label={isFavorite ? t('saved') : t('save')}
      >
        <svg
          className={cn('w-4 h-4 transition-transform', isLoading && 'animate-pulse')}
          fill={isFavorite ? 'currentColor' : 'none'}
          stroke="currentColor"
          strokeWidth={2}
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
          />
        </svg>
      </button>
    );
  }

  return (
    <button
      onClick={toggleFavorite}
      disabled={isLoading}
      className={cn(
        'flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-medium transition-all',
        isFavorite
          ? 'bg-red-50 text-red-600 hover:bg-red-100'
          : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
        className,
      )}
      aria-label={isFavorite ? t('saved') : t('save')}
    >
      <svg
        className={cn('w-5 h-5 transition-transform', isLoading && 'animate-pulse')}
        fill={isFavorite ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth={2}
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
        />
      </svg>
      {isFavorite ? t('saved') : t('save')}
    </button>
  );
}
